// src/components/lesson/diagrams/BoxPlot.tsx
//
// Box-and-whisker plot for SAT "One-Variable Data" questions (median, quartiles,
// IQR, range, spread). Five-number summary over a horizontal number-line axis.
//
// Params:
//   min, q1, median, q3, max   (numbers — the five-number summary; required for
//                               a meaningful plot, but defaults keep it from
//                               crashing)
//   axisMin?, axisMax?         (axis range; default = min/max with padding)
//   showValues?: boolean       (label the five numbers; default true)
//   label?: string

type BoxPlotParams = {
  min?: number
  q1?: number
  median?: number
  q3?: number
  max?: number
  axisMin?: number
  axisMax?: number
  showValues?: boolean
  label?: string
}

const W = 360
const H = 150
const PAD = 30
const AXIS_Y = 96
const BOX_TOP = 50
const BOX_BOT = 84
const stroke = 'rgb(34 211 238)'
const fill = 'rgb(34 211 238 / 0.12)'
const axisColor = 'rgb(100 116 139)'
const labelColor = 'rgb(251 191 36)'
const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)

export default function BoxPlot({ params }: { params: Record<string, unknown> }) {
  const p = params as BoxPlotParams
  const min = num(p.min, 0)
  const q1 = num(p.q1, 1)
  const median = num(p.median, 2)
  const q3 = num(p.q3, 3)
  const max = num(p.max, 4)
  const lo = num(p.axisMin, min)
  const hi = num(p.axisMax, max)
  const span = hi - lo || 1
  const showValues = p.showValues !== false

  const vx = (v: number) => PAD + ((v - lo) / span) * (W - 2 * PAD)

  // Axis ticks: ~6 evenly-spaced integer-ish marks.
  const ticks: number[] = []
  const step = niceStep(span / 5)
  for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step)
    ticks.push(Math.round(t * 100) / 100)

  const cy = (BOX_TOP + BOX_BOT) / 2

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Box plot'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Axis */}
      <line x1={PAD} y1={AXIS_Y} x2={W - PAD} y2={AXIS_Y} stroke={axisColor} strokeWidth="1" />
      {ticks.map((t, i) => (
        <g key={`tk-${i}`}>
          <line
            x1={vx(t)}
            y1={AXIS_Y}
            x2={vx(t)}
            y2={AXIS_Y + 4}
            stroke={axisColor}
            strokeWidth="1"
          />
          <text x={vx(t)} y={AXIS_Y + 16} fontSize="9" textAnchor="middle" fill={axisColor}>
            {t}
          </text>
        </g>
      ))}

      {/* Whiskers (min→q1, q3→max) with end caps */}
      <line x1={vx(min)} y1={cy} x2={vx(q1)} y2={cy} stroke={stroke} strokeWidth="1.5" />
      <line x1={vx(q3)} y1={cy} x2={vx(max)} y2={cy} stroke={stroke} strokeWidth="1.5" />
      <line
        x1={vx(min)}
        y1={BOX_TOP + 4}
        x2={vx(min)}
        y2={BOX_BOT - 4}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <line
        x1={vx(max)}
        y1={BOX_TOP + 4}
        x2={vx(max)}
        y2={BOX_BOT - 4}
        stroke={stroke}
        strokeWidth="1.5"
      />

      {/* Box (q1→q3) + median line */}
      <rect
        x={vx(q1)}
        y={BOX_TOP}
        width={Math.max(vx(q3) - vx(q1), 1)}
        height={BOX_BOT - BOX_TOP}
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <line
        x1={vx(median)}
        y1={BOX_TOP}
        x2={vx(median)}
        y2={BOX_BOT}
        stroke={stroke}
        strokeWidth="2"
      />

      {/* Five-number labels above the plot */}
      {showValues &&
        (
          [
            [min, 'min'],
            [q1, 'Q1'],
            [median, 'med'],
            [q3, 'Q3'],
            [max, 'max'],
          ] as [number, string][]
        ).map(([v], i) => (
          <text
            key={`v-${i}`}
            x={vx(v)}
            y={BOX_TOP - 6}
            fontSize="9"
            fontWeight="600"
            textAnchor="middle"
            fill={labelColor}
          >
            {v}
          </text>
        ))}
    </svg>
  )
}

/** Round a step up to a "nice" 1/2/5 × 10^k value for readable ticks. */
function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(raw)))
  const f = raw / pow
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10
  return nice * pow
}
